/** @jsxImportSource @emotion/react */
import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { AttachmentPreviewModalContent, AttachmentPreviewOverlay } from './AttachmentPreviewModal.styled';
import { AttachmentLabel } from './AttachmentPreviewModal.styled';
import { AttachmentIconAndLabelWrapper } from './AttachmentPreviewModal.styled';
import { AttachmentItem } from './AttachmentPreviewModal.styled';
import { AttachmentWrapper } from './AttachmentPreviewModal.styled';
import { AttachmentContext } from './AttachmentPreviewModal.styled';
import { ButtonsWrapper, CancelButton } from './NoteModal.styled';
import { AddMoreFilesButton } from './AttachmentPreviewModal.styled';
import { useKeyboardHeight } from '../../../hooks/use-keyboard-height';

type AttachmentData = {
  type: 'file' | 'photo' | 'link';
  data: File | File[] | string;
};

type AttachmentPreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  attachmentData: AttachmentData | null;
  onSend: (data: AttachmentData, contextText: string) => Promise<void> | void;
  onAddMoreFiles: () => void;
  uploadProgress?: number | null;
};

export const AttachmentPreviewModal: React.FC<AttachmentPreviewModalProps> = ({
  isOpen,
  onClose,
  attachmentData,
  onSend,
  onAddMoreFiles,
  uploadProgress,
}) => {
  const [contextText, setContextText] = React.useState('');
  const [isSending, setIsSending] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const { height: keyboardBottom, open: isKeyboardOpen } = useKeyboardHeight();

  const handleSend = React.useCallback(async () => {
    if (!attachmentData || isSending) return;
    setIsSending(true);
    try {
      await onSend(attachmentData, contextText.trim());
      setContextText('');
    } catch {
      // Error toast handled by caller (chat.tsx)
    } finally {
      setIsSending(false);
    }
  }, [attachmentData, onSend, contextText, isSending]);

  const handleClose = React.useCallback(() => {
    if (isSending) return;
    setContextText('');
    onClose();
  }, [onClose, isSending]);

  const handleAddMoreFiles = React.useCallback(() => {
    onAddMoreFiles();
  }, [onAddMoreFiles]);

  // Block background touch-scroll, but allow scroll inside the modal content
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!isOpen) return;

    const preventScroll = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Allow scroll inside the modal content
      if (contentRef.current?.contains(target)) return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [isOpen]);

  const handleOverlayClick = React.useCallback(() => {
    if (isKeyboardOpen) {
      textareaRef.current?.blur();
    } else {
      handleClose();
    }
  }, [isKeyboardOpen, handleClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <AttachmentPreviewOverlay
          keyboardBottom={keyboardBottom}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
        >
          <AttachmentPreviewModalContent
            ref={contentRef}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{ touchAction: 'pan-y' }}
          >
            <AttachmentWrapper>
              {attachmentData && (
                <>
                  {attachmentData.type === 'file' && (
                    <>
                      {(Array.isArray(attachmentData.data) ? attachmentData.data : [attachmentData.data as File]).map((file, index) => (
                        <AttachmentItem key={index}>
                          <AttachmentIconAndLabelWrapper>
                            <img src='/file.svg' alt="file" />
                            <AttachmentLabel>{file.name} ({(file.size / 1024).toFixed(1)} KB)</AttachmentLabel>
                          </AttachmentIconAndLabelWrapper>
                        </AttachmentItem>
                      ))}
                    </>
                  )}
                  {attachmentData.type === 'photo' && (
                    <AttachmentItem>
                      <AttachmentIconAndLabelWrapper>
                        <img src='/file.svg' alt="photo" />
                        <AttachmentLabel>{(attachmentData.data as File).name} ({((attachmentData.data as File).size / 1024).toFixed(1)} KB)</AttachmentLabel>
                      </AttachmentIconAndLabelWrapper>
                    </AttachmentItem>
                  )}
                  {attachmentData.type === 'link' && (
                    <AttachmentItem>
                      <AttachmentIconAndLabelWrapper>
                        <img src='/file.svg' alt="link" />
                        <AttachmentLabel>{attachmentData.data as string}</AttachmentLabel>
                      </AttachmentIconAndLabelWrapper>
                    </AttachmentItem>
                  )}
                </>
              )}
            </AttachmentWrapper>
            <AttachmentContext
              ref={textareaRef}
              placeholder="Add context (optional)…"
              value={contextText}
              onChange={(e) => setContextText(e.target.value)}
            />
            {uploadProgress != null && uploadProgress > 0 && (
              <div style={{ width: "100%", padding: "0 0 8px" }}>
                <div
                  style={{
                    height: 3,
                    background: "#5E84ED",
                    borderRadius: 9999,
                    width: `${uploadProgress}%`,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            )}
            <ButtonsWrapper>
                <CancelButton onClick={handleClose}>
                  Cancel
                </CancelButton>
                <AddMoreFilesButton onClick={handleAddMoreFiles}>
                    + Add more files
                </AddMoreFilesButton>
                <img
                  src="/send-with-bg.svg"
                  alt="Send"
                  onClick={handleSend}
                  style={isSending ? { cursor: 'default', opacity: 0.5, pointerEvents: 'none' } : { cursor: 'pointer' }}
                />
            </ButtonsWrapper>
          </AttachmentPreviewModalContent>
        </AttachmentPreviewOverlay>
      )}
    </AnimatePresence>
  );
};
